<template>
  <div class="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex">
    <!-- Loading Overlay -->
    <div
      v-if="load"
      class="absolute inset-0 bg-white/90 dark:bg-gray-900/90 flex items-center justify-center z-50"
    >
      <div class="bg-white dark:bg-gray-800 rounded shadow border border-gray-200 dark:border-gray-700 p-4 flex items-center space-x-3">
        <div class="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        <span class="text-sm text-gray-700 dark:text-gray-300">Hesaplanıyor...</span>
      </div>
    </div>

    <hgsDetail
      name="hgs-detail-modal-full-screen"
      :List="tollsList"
      :bridges="bridgesList"
    />

    <!-- Modern Layout -->
    <div class="w-full h-full flex">
      <!-- Left Sidebar - Route Details -->
      <RouteDetailsSidebar
        :collapsed="sidebarCollapsed"
        :totalKilometer="totalKilometer"
        :totalMinutes="totalMinutes"
        :totalCost="route.totalCost"
        :fuelPrice="fuelPrice"
        :routeType="routeType"
        :vehicleTypeId="vehicleTypeId"
        :isToll="isToll"
        :isExplosive="isExplosive"
        :isShip="isShip"
        :vehicleTypes="vehicleTypes"
        :rectangles="rectangles"
        :hasWaypoints="waypointsList.filter((r) => r.type == 'route').length > 0"
        @toggle-sidebar="toggleSidebar"
        @close="closeFullPanel"
        @update:routeType="routeType = $event"
        @update:vehicleTypeId="handleVehicleTypeChange"
        @update:isToll="isToll = $event"
        @update:isExplosive="isExplosive = $event"
        @update:isShip="isShip = $event"
        @settings-updated="getRoute"
        @add-rectangle="addRectangle"
        @remove-rectangle="removeRectangle"
        @recalculate="getRoute"
        @undo-waypoint="undoWaypoint"
        @show-modal="$modal.show('hgs-detail-modal-full-screen')"
      />

      <!-- Main Content Area -->
      <div class="flex-1 flex flex-col">
        <!-- Map and Right Sidebar Container -->
        <div class="flex-1 flex relative">
          <!-- Map Area -->
          <RouteMapContainer
            :startPosition="startPosition"
            :endPosition="endPosition"
            :center.sync="center"
            :waypoints="waypoints"
            :polyLineList="polyLineList"
            :tollsList="tollsList"
            :bridgesList="bridgesList"
            :customPoints="customPoints"
            :rectangles="rectangles"
            :circleCenter="circleCenter"
            :isDragging="isDragging"
            :locationPinImages="locationPinImages"
            ref="routeMap"
            @mouse-move="handleMouseMove"
            @enable-dragging="enableDragging"
            @finalize-position="finalizePosition"
            @bounds-changed="onBoundsChanged"
          />

          <!-- Right Sidebar - Toll Roads -->
          <TollRoadsSidebar
            :pricingRoadList="pricingRoadList"
            :avoidList="avoidList"
            :rectangles="rectangles"
            @add-avoid-point="addAvoidPoint"
            @remove-avoid-list="removeAvoidList"
            @remove-rectangle="removeRectangle"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { routeEngine } from "@/networking/routeEngine";

import { ToggleButton } from "vue-js-toggle-button";

import diffInput from "@/components/general/diffInput.vue";
import asyncBtn from "@/components/general/asyncBtn.vue";
import gmapInputVue from "@/components/general/gmapInput.vue";
import hgsDetail from "./detail/hgs-detail.vue";

// Import modular components
import RouteDetailsSidebar from "./components/RouteDetailsSidebar.vue";
import RouteMapContainer from "./components/RouteMapContainer.vue";
import TollRoadsSidebar from "./components/TollRoadsSidebar.vue";

import { decode } from "../../../utils/encoder";
import { calculator } from "@/utils/cost";

export default {
  name: "full-routes",
  props: [
    "waypoints",
    "startPosition",
    "endPosition",
    "polyLine",
    "forbidenBounds",
    "avoidPointsList",
    "tolls",
    "kilometer",
    "routes",
    "bridges",
    "isExplosiveDefault",
    "customerStateId",
    "outpointStateId",
    "outpointId",
    "customerId",
    "custompoints",
    "initialVehicleTypeId",
    "initialRouteType",
    "initialIsToll",
    "initialIsShip",
    "initialFuelRatio",
  ],
  components: {
    diffInput,
    ToggleButton,
    asyncBtn,
    hgsDetail,
    gmapInputVue,
    RouteDetailsSidebar,
    RouteMapContainer,
    TollRoadsSidebar,
  },

  data() {
    return {
      center: null,
      totalKilometer: 0,
      totalMinutes: 0,
      loadState: false,
      isToll: this.initialIsToll !== undefined ? this.initialIsToll : 1,
      isShip: this.initialIsShip !== undefined ? this.initialIsShip : 0,
      isAsphalt: 0,
      isExplosive: 0,
      sidebarCollapsed: false,
      vehicleTypeId: this.initialVehicleTypeId || 5,
      routeType: this.initialRouteType || 1,
      fuelRatio: this.initialFuelRatio || 35,
      vehicleTypes: [],
      customPoints: [],
      fuelPrice: 1,
      locationPinImages: [
        require("@/assets/marker/location-pin.png"),
        require("@/assets/marker/location-pin-2.png"),
        require("@/assets/marker/location-pin-3.png"),
        require("@/assets/marker/location-pin-4.png"),
        require("@/assets/marker/location-pin-5.png"),
      ],

      waypointsList: [],
      polyLineList: [],
      tollsList: [],
      rectangles: [], // Multiple restricted areas
      pricingRoadList: [],
      avoidList: [],

      bridgesList: [],
      load: false,

      route: {},

      circleCenter: { lat: 41.015137, lng: 28.97953 },
      isDragging: false,
      isCircleVisible: false,
    };
  },
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    },
    handleVehicleTypeChange(newVehicleTypeId) {
      this.vehicleTypeId = newVehicleTypeId;

      // Update fuelRatio when vehicleTypeId changes
      const fuelRatioMap = {
        1: 20,  // Otomobil
        2: 25,  // Minivan
        3: 30,  // Kamyonet
        4: 32,  // Kamyon
        5: 35,  // TIR
      };
      this.fuelRatio = fuelRatioMap[newVehicleTypeId] || 35;
    },
    updateSettings(settings) {
      Object.assign(this, settings);

      // Update fuelRatio when vehicleTypeId changes
      if (settings.vehicleTypeId !== undefined) {
        const fuelRatioMap = {
          1: 20,  // Otomobil
          2: 25,  // Minivan
          3: 30,  // Kamyonet
          4: 32,  // Kamyon
          5: 35,  // TIR
        };
        this.fuelRatio = fuelRatioMap[settings.vehicleTypeId] || 35;
      }
    },
    Money(value) {
      return Number(value).toFixed(2);
    },
    handleMouseMove(event) {
      const clickPoint = event.latLng;
      const polyline = new google.maps.Polyline({
        path: this.polyLineList,
      });
      if (
        this.isDragging ||
        google.maps.geometry.poly.isLocationOnEdge(clickPoint, polyline, 10e-4)
      ) {
        // Hassasiyeti ayarlayın
        this.circleCenter = {
          lat: clickPoint.lat(),
          lng: clickPoint.lng(),
        };
      }
    },
    enableDragging(event) {
      if (!this.$refs.routeMap || !this.$refs.routeMap.$refs.map) return;
      
      const bounds = this.$refs.routeMap.$refs.map.$mapObject.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const scale = Math.pow(2, this.$refs.routeMap.$refs.map.$mapObject.getZoom());
      const nw = new google.maps.LatLng(ne.lat(), sw.lng());
      const worldCoordinateNW = this.$refs.routeMap.$refs.map.$mapObject
        .getProjection()
        .fromLatLngToPoint(nw);
      const worldCoordinate = new google.maps.Point(
        worldCoordinateNW.x + event.offsetX / scale,
        worldCoordinateNW.y + event.offsetY / scale
      );
      const latLng = this.$refs.routeMap.$refs.map.$mapObject
        .getProjection()
        .fromPointToLatLng(worldCoordinate);

      const clickPoint = latLng;
      const polyline = new google.maps.Polyline({
        path: this.polyLineList,
      });

      if (
        google.maps.geometry.poly.isLocationOnEdge(clickPoint, polyline, 10e-3)
      ) {
        // Hassasiyeti ayarlayın
        const map = this.$refs.routeMap.$refs.map.$mapObject;
        map.setOptions({ draggable: false });
        this.isDragging = true;
        this.isCircleVisible = true; // Circle'ı sadece sürüklenme sırasında göster
      }
    },
    finalizePosition(event) {
      if (this.isDragging && this.$refs.routeMap && this.$refs.routeMap.$refs.map) {
        this.waypointsList.push({
          type: "route",
          step: this.waypointsList.filter((r) => r.type == "route").length + 1,
          lat: this.circleCenter.lat,
          lng: this.circleCenter.lng,
        });

        const map = this.$refs.routeMap.$refs.map.$mapObject;
        map.setOptions({ draggable: true });

        this.isDragging = false;
        this.getRoute(true);
        // Burada yeni pozisyonu kullanarak rota güncelleme işlemi yapın
      }
    },
    addAvoidPoint(item) {
      const params = {
        id: item.id,
        name: item.name,
        typeId: item.typeId,
        price: item.price,
      };
      this.avoidList.push(params);
      this.getRoute();
    },
    removeAvoidList(item) {
      this.avoidList = this.avoidList.filter((i) => i.id != item.id);
      this.getRoute();
    },
    undoWaypoint() {
      // isWaypoint olmayan waypointleri filtrele
      const nonWaypointList = this.waypointsList.filter(
        (item) => !item.isWaypoint
      );

      // Filtrelenmiş listeden en yüksek adımlı waypoint'i bul
      const result = nonWaypointList.reduce(
        (acc, item, index) => {
          if (
            item.type === "route" &&
            item.step !== undefined &&
            item.step > acc.maxStep
          ) {
            return { maxStep: item.step, maxStepIndex: index };
          }
          return acc;
        },
        { maxStep: -Infinity, maxStepIndex: -1 }
      );

      console.log(
        "Filtrelenmiş listeden en yüksek adımlı waypoint'i bul : ",
        result
      );

      // Geçerli bir waypoint bulunup bulunmadığını kontrol et
      if (result.maxStepIndex === -1) {
        console.warn("No valid waypoint found to undo.");
        return;
      }

      // Orijinal listedeki en yüksek adımlı waypoint'in indeksini bul
      const originalIndex = this.waypointsList.findIndex(
        (item) =>
          item &&
          nonWaypointList[result.maxStepIndex] &&
          item.id === nonWaypointList[result.maxStepIndex].id &&
          item.step === nonWaypointList[result.maxStepIndex].step
      );

      // Eğer geçerli bir indeks bulunmuşsa bu waypoint'i sil
      if (originalIndex > -1) {
        this.waypointsList.splice(originalIndex, 1);
        this.getRoute(true);
      } else {
        console.warn("Original index not found.");
      }
    },
    changeDrag(val) {
      this.waypointsList.push({
        type: "route",
        step: this.waypointsList.filter((r) => r.type == "route").length + 1,
        lat: val.latLng.lat(),
        lng: val.latLng.lng(),
      });
      this.getRoute(true);
    },
    getTimeDetail(mins) {
      let minutes = parseFloat(mins);
      const h = (minutes / 60) | 0,
        m = minutes % 60 | 0;

      return h + " Saat" + " " + m + " Dakika";
    },
    async getManagmentCost() {
      const montlyKilometer = this.route.kilometer;

      const expenseDetail = await calculator(
        this.$store.state.userData.token,
        1,
        montlyKilometer * 12,
        null,
        null,
        montlyKilometer,
        1,
        true,
        1,
        this.dayCount
      );

      const { fuelTotalPrice, totalAdbluePrice } = expenseDetail;
      this.fuelPrice = fuelTotalPrice + totalAdbluePrice;
    },
    async getVehicleTypes() {
      const result = await axios.get(routeEngine.getVehicleTypes, {
        headers: {
          Authorization: "Bareer " + this.$store.state.routeEngineToken,
        },
      });
      this.vehicleTypes = result.data.data;
    },
    addRectangle() {
      if (this.$refs.routeMap && this.$refs.routeMap.$refs.map) {
        const map = this.$refs.routeMap.$refs.map.$mapObject;
        const center = map.getCenter();
        const id = `restricted-${Date.now()}`;
        const bounds = {
          north: center.lat() + 0.05,
          south: center.lat() - 0.05,
          east: center.lng() + 0.05,
          west: center.lng() - 0.05,
        };
        this.rectangles.push({
          id,
          bounds
        });
      }
    },
    removeRectangle(id) {
      const index = this.rectangles.findIndex(r => r.id === id);
      if (index !== -1) {
        this.rectangles.splice(index, 1);
        this.removeAvoidList({ id });
      }
    },
    onBoundsChanged(val, id) {
      // Update the specific rectangle's bounds
      const rectangleIndex = this.rectangles.findIndex(r => r.id === id);
      if (rectangleIndex !== -1) {
        this.rectangles[rectangleIndex].bounds = val;
      }

      // Update avoidList (but don't trigger route calculation)
      const findItem = this.avoidList.findIndex((r) => r.id == id);
      const firstValue = Object.values(val)[0];
      const secondValue = Object.values(val)[1];
      if (findItem === -1) {
        this.avoidList.push({
          id,
          name: `Yasaklı Alan ${this.rectangles.length}`,
          typeId: 4,
          positions: [
            {
              lng: String(secondValue.hi),
              lat: String(firstValue.hi),
            },
            {
              lng: String(secondValue.lo),
              lat: String(firstValue.lo),
            },
          ],
        });
      } else {
        this.avoidList[findItem].positions = [
          {
            lng: String(secondValue.hi),
            lat: String(firstValue.hi),
          },
          {
            lng: String(secondValue.lo),
            lat: String(firstValue.lo),
          },
        ];
      }
      // Route calculation will only happen when user clicks "Rotayı Hesapla" button
    },
    getRoute(loadState = true) {
      this.load = loadState;

      const newWaypointsList = this.waypointsList.map((item) => ({
        lat: item.lat,
        lng: item.lng,
      }));

      const newAvoidpointsList = this.avoidList.map(
        ({ id, name, typeId, positions }) => {
          const avoidPoint = { id, name };

          if (typeId) {
            avoidPoint.typeId = typeId;
          }
          if (positions) {
            avoidPoint.positions = positions;
          }

          return avoidPoint;
        }
      );

      axios
        .post(
          routeEngine.generate,
          {
            from: this.startPosition,
            to: this.endPosition,
            waypoints: newWaypointsList,
            avoidPoints: newAvoidpointsList,
            isToll: this.isToll,
            vehicleTypeId: this.vehicleTypeId,
            routeType: this.routeType == 1 ? "fast" : "short",
            shippedHazardousGoods: this.isExplosive,
            isShip: this.isShip,
            isAsphalt: this.isAsphalt,
            fuelRatio: this.fuelRatio,
            isPolyline: 1,
            isOptimized:1
          },
          {
            headers: {
              Authorization: "Bareer " + this.$store.state.routeEngineToken,
            },
          }
        )
        .then((res) => {
          this.route = res.data.data;
          // this.route = res.data.data[0];

          this.$emit("changeEndPosition", this.endPosition);
          this.$emit("changeCustomerState", this.customerStateId);
          this.$emit("changeCustomerId", this.customerId);

          this.$emit("changeOutpointStateId", this.outpointStateId);
          this.$emit("changeOutpointId", this.outpointId);
          this.$emit("changeStartPosition", this.startPosition);

          this.routeGenerate();
          this.getManagmentCost();
        })
        .catch((err) => {
          console.log(err.response);
          this.errorBox(err.response);
        })
        .finally(() => {
          this.load = false;

          if (this.$refs.routeMap && this.$refs.routeMap.$refs.map) {
            const map = this.$refs.routeMap.$refs.map.$mapObject;
            map.setOptions({ draggable: true });
          }
          this.isDragging = false;
          this.circleCenter = null;
        });
    },
    routeGenerate() {
      const data = this.route;

      this.pricingRoadList = [];
      this.polyLineList = decode(data.routePolyline).polyline;

      const parsedCostDetail = data.costDetail.map((item) => {
        return {
          ...item,
          startLat: Number(item.startLat),
          startLng: Number(item.startLng),
          endLat: Number(item.endLat),
          endLng: Number(item.endLng),
        };
      });
      this.customPoints = Array.isArray(data?.customPoints)
        ? data?.customPoints
        : [];
      const tollsArr = parsedCostDetail.filter(
        (item) => item.typeName !== "Bridge"
      );
      const bridgeArr = parsedCostDetail.filter(
        (item) => item.typeName === "Bridge"
      );

      this.tollsList = tollsArr;
      this.bridgesList = bridgeArr;

      this.pricingRoadList.push(...tollsArr);
      this.pricingRoadList.push(...bridgeArr);

      this.totalKilometer = data.kilometer;
      this.totalMinutes = data.totalMinutes;

      this.$emit("change", {
        cost: {
          fuel: data.fuelAmount,
          cash: data.totalCost,
        },
        polyLine: JSON.stringify({
          startPosition: this.startPosition,
          waypoints: this.waypoints,
          endPosition: this.endPosition,
        }),
        totalMinutes: data.totalMinutes,
        // navigationLink: data.navigationLink,
        navigationLink: data.routeViewUrl,
        totalKilometer: data.kilometer,
        tolls: tollsArr,
        bridges: bridgeArr,
      });
    },
    removePlace(item, index) {
      if (item.type == "route") {
        this.waypointsList.splice(index, 1);
        this.getRoute(true);
      }
    },
    getPlace(val) {
      this.waypointsList.push({
        lat: val.latLng.lat(),
        lng: val.latLng.lng(),
      });

      this.getRoute(true);
    },
    addMarker(val) {
      if (this.waypointsList.length < 10) {
        this.waypointsList.push({
          type: "route",
          lat: val.latLng.lat(),
          lng: val.latLng.lng(),
        });
        this.getRoute(true);
      } else {
        this.swalBox(
          "warning",
          "UYARI",
          "10'dan fazla ara nokta ekleyemezsiniz!",
          false,
          "Tamam"
        );
      }
    },
    setPlace(val, index) {
      this.waypointsList[index] = {
        type: this.waypointsList[index].type,
        lat: val.latLng.lat(),
        lng: val.latLng.lng(),
      };
      this.getRoute(true);
    },
    closeFullPanel() {
      this.$parent.route = this.route;
      this.$parent.waypoints = this.waypointsList;
      this.$parent.avoidPointsList = this.avoidList;
      this.$parent.bridges = this.bridgesList;
      this.$parent.tolls = this.tollsList;
      this.$parent.polyLine = this.polyLineList;
      // For backwards compatibility, send first rectangle bounds if exists
      this.$parent.forbidenBounds = this.rectangles.length > 0 ? this.rectangles[0].bounds : null;
      this.$emit("hidePanel", true);
    },
    changeEndLocation(val) {
      this.endPosition = val;
      // this.getRoute();
    },
    changeStartLocation(val) {
      this.startPosition = val;
      // this.getRoute();
    },
    handlePolylineClick(event) {
      this.circleCenter = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
    },
  },
  async created() {
    // Initialize with existing forbidden bounds from parent if provided
    if (this.forbidenBounds) {
      const id = 'restricted-initial';
      this.rectangles.push({
        id,
        bounds: this.forbidenBounds
      });

      setTimeout(() => {
        this.onBoundsChanged(this.forbidenBounds, id);
      }, 1000);
    }

    this.getManagmentCost();

    // Wait for vehicle types to load before proceeding
    await this.getVehicleTypes();

    // Ensure vehicleTypeId is set correctly after vehicle types are loaded
    if (!this.vehicleTypeId || !this.vehicleTypes.find(v => v.id === this.vehicleTypeId)) {
      this.vehicleTypeId = this.initialVehicleTypeId || 5;
    }

    this.center = this.startPosition;
    this.totalKilometer = this.routes.kilometer;
    this.totalMinutes = this.routes.totalMinutes;
    this.waypointsList = this.waypoints;
    this.avoidList = this.avoidPointsList;
    this.tollsList = this.tolls;
    this.bridgesList = this.bridges;
    this.pricingRoadList.push(...this.tolls);
    this.pricingRoadList.push(...this.bridges);
    this.polyLineList = this.polyLine;
    this.isExplosive = this.isExplosiveDefault
      ? this.isExplosiveDefault == true
        ? 1
        : 0
      : 0;
    this.customPoints = this.custompoints;
    this.route = this.routes;

    this.getRoute();
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.routeMap && this.$refs.routeMap.$refs.map) {
        this.$refs.routeMap.$refs.map.$mapPromise.then((map) => {
          const polyline = new google.maps.Polyline({
            path: this.polylinePath,
            map: map,
            strokeColor: "#FF0000",
          });

          // Polyline'a bir tıklama işleyicisi ekleyin
          polyline.addListener("click", (event) => {
            this.handlePolylineClick(event);
          });
        });
      }
    });
  },
};
</script>

<style scoped>
/* Modern Marker Label Styles */
:global(.marker-label-modern) {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 500;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 5px;
}

/* Custom scrollbar for better UX */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}
</style>
